/**
 * @file services/api/notificationService.ts
 * @brief Notification service for API calls
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from './endpoints';

function unwrapResponse(response: any) {
  if (response?.data?.success === true && 'data' in response.data) {
    return response.data.data;
  }
  if (response?.data) {
    return response.data;
  }
  return response;
}

export interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  readAt?: string;
  linkType?: 'task' | 'project' | 'review' | 'leave' | 'ticket';
  linkId?: string;
  priority?: 'low' | 'medium' | 'high';
  createdAt: string;
  updatedAt?: string;
}

export interface NotificationPreferences {
  userId: string;
  email: boolean;
  taskReminders: boolean;
  deadlineAlerts: boolean;
  reviewNotifications: boolean;
}

export const notificationService = {
  /**
   * List notifications with pagination
   */
  async listNotifications(page = 1, pageSize = 20): Promise<{ 
    items: Notification[]; 
    total: number; 
    page: number; 
    pageSize: number;
    totalPages: number;
    hasMore: boolean;
  }> {
    const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.LIST, {
      params: { page, pageSize },
    });
    const data = unwrapResponse(response);
    return data as {
      items: Notification[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
      hasMore: boolean;
    };
  },

  /**
   * Get unread notifications
   */
  async getUnread(): Promise<{ items: Notification[]; count: number }> {
    const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.UNREAD);
    const data = unwrapResponse(response);
    return data as { items: Notification[]; count: number };
  },

  /**
   * Mark notification as read
   */
  async markRead(notificationId: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId));
  },

  /**
   * Mark all notifications as read
   */
  async markAllRead(): Promise<void> {
    await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);
  },

  /**
   * Clear/delete all read notifications
   */
  async clearReadNotifications(): Promise<{ deleted: number }> {
    const response = await apiClient.delete(API_ENDPOINTS.NOTIFICATIONS.CLEAR_READ);
    const data = unwrapResponse(response);
    return data as { deleted: number };
  },

  /**
   * Get notification preferences
   */
  async getPreferences(): Promise<NotificationPreferences> {
    const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES);
    const data = unwrapResponse(response);
    return data as NotificationPreferences;
  },

  /**
   * Update notification preferences
   */
  async updatePreferences(preferences: Record<string, boolean>): Promise<NotificationPreferences> {
    const response = await apiClient.put(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES, preferences);
    const data = unwrapResponse(response);
    return data as NotificationPreferences;
  },
};
